import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { CalendarDays, FileText, Receipt, Clock, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";

const LEAVE_META = [
  { key: "conges_payes", label: "Congés payés", color: "#0066FF", bg: "bg-[#0066FF]/10", text: "text-[#0066FF]" },
  { key: "rtt", label: "RTT", color: "#7C3AED", bg: "bg-violet-100", text: "text-violet-700" },
  { key: "maladie", label: "Maladie", color: "#059669", bg: "bg-emerald-100", text: "text-emerald-700" },
];

function fmtDays(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
}

const Stat = ({ icon: Icon, label, value, hint, color = "blue", testId }) => {
  const colors = {
    blue: "bg-[#0066FF]/10 text-[#0066FF]",
    amber: "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <Card className="p-4 sm:p-6 border-slate-200" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 truncate">{label}</div>
          <div className="mt-1 sm:mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">{value}</div>
          {hint && <div className="text-[11px] sm:text-xs text-slate-500 mt-1 truncate">{hint}</div>}
        </div>
        <div className={`h-9 w-9 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center shrink-0 ${colors[color]}`}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
      </div>
    </Card>
  );
};

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/employee").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const soldes = stats?.soldes || {};
  const reserves = stats?.reserves || {};

  return (
    <div>
      <PageHeader
        title={`Bonjour, ${user?.full_name?.split(" ")[0] || ""} 👋`}
        subtitle="Bienvenue sur votre espace personnel. Retrouvez vos soldes, congés, bulletins et attestations en un clin d'œil."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        <Stat testId="stat-demandes-cours" icon={Clock} label="Demandes en cours" value={stats?.pending_leaves ?? "—"} hint="Congés en attente" color="amber" />
        <Stat testId="stat-approuves" icon={CalendarDays} label="Congés approuvés" value={stats?.approved_leaves ?? "—"} hint="Total validé" color="blue" />
        <Stat testId="stat-bulletins" icon={Receipt} label="Bulletins de paie" value={stats?.payslips ?? "—"} hint="Disponibles" color="emerald" />
        <Stat testId="stat-attestations-cours" icon={FileText} label="Attestations en cours" value={stats?.certs_pending ?? "—"} hint="En attente RH" color="slate" />
      </div>

      <div className="mt-8">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Mes soldes de congés</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {LEAVE_META.map((t) => {
            const balance = soldes[t.key] ?? 0;
            const reserved = reserves[t.key] ?? 0;
            const available = Math.max(0, balance - reserved);
            const pct = balance > 0 ? Math.min(100, (available / balance) * 100) : 0;
            return (
              <Card key={t.key} className="p-6 border-slate-200" data-testid={`solde-${t.key}`}>
                <div className="flex items-center justify-between">
                  <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold ${t.bg} ${t.text}`}>{t.label}</div>
                  <TrendingUp className={`h-4 w-4 ${t.text}`} />
                </div>
                <div className="mt-4 flex items-end gap-2">
                  <span className="font-heading text-5xl font-bold" style={{ color: t.color }}>{fmtDays(available)}</span>
                  <span className="text-slate-500 text-sm mb-2">/ {fmtDays(balance)} j</span>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: t.color }} />
                </div>
                <div className="mt-3 text-xs text-slate-500 space-y-0.5">
                  <div>Solde acquis : <b className="text-slate-700">{fmtDays(balance)}</b> j</div>
                  {reserved > 0 && <div>En attente/pris : <b className="text-slate-700">{fmtDays(reserved)}</b> j</div>}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <Card className="mt-8 p-6 border-slate-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Une démarche à effectuer ?</h2>
            <p className="text-sm text-slate-500 mt-1">Congés, attestation de travail, bulletins de paie : tout est à portée de clic.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-[#0066FF] hover:bg-[#0052CC]"><Link to="/employe/conges" data-testid="cta-nouvelle-demande">Demander un congé</Link></Button>
            <Button asChild variant="outline"><Link to="/employe/attestations">Attestation de travail</Link></Button>
            <Button asChild variant="outline"><Link to="/employe/bulletins">Mes bulletins</Link></Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
